import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureQuery } from '@/hooks/useSecureQuery';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Plus, Edit, Play, CheckCircle, AlertCircle, XCircle, Clock } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSecureMutation } from '@/hooks/useSecureMutation';
import { toast } from 'sonner';
import { ProductThumbnail } from '@/components/ui/product-thumbnail';

const productionRunSchema = z.object({
  finished_product_id: z.string().min(1, 'Please select a finished product'),
  quantity_to_produce: z.number().min(1, 'Quantity must be at least 1'),
  status: z.enum(['planned', 'in_progress', 'completed', 'cancelled']).default('planned'),
});

const editProductionRunSchema = z.object({
  status: z.enum(['planned', 'in_progress', 'completed', 'cancelled']),
  quantity_to_produce: z.number().min(1, 'Quantity must be at least 1').optional(),
  notes: z.string().optional(),
});

type ProductionRunFormData = z.infer<typeof productionRunSchema>;
type EditProductionRunFormData = z.infer<typeof editProductionRunSchema>;

export const ProductionRuns = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingRun, setEditingRun] = useState<any>(null);
  const [processingRunIds, setProcessingRunIds] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const { userRole, isSuperAdmin } = usePermissions();
  
  // Check if user can modify production runs (managers, admins, super admins)
  const canModify = isSuperAdmin || ['admin', 'manager'].includes(userRole);

  const { data: runs = [], isLoading, refetch: refetchProductionRuns } = useSecureQuery(
    ['production-runs'],
    async () => {
      const { data, error } = await supabase
        .from('production_runs')
        .select(`
          *,
          finished_product:finished_product_id(name, sku, image_url)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    {
      requiresAuth: true,
    }
  );

  const { data: finishedProducts = [] } = useSecureQuery(
    ['finished-products'],
    async () => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('id, name, sku, image_url')
        .eq('type', 'finished_product')
        .order('name');
      
      if (error) throw error;
      return data;
    },
    {
      requiresAuth: true,
    }
  );

  const form = useForm<ProductionRunFormData>({
    resolver: zodResolver(productionRunSchema),
    defaultValues: {
      status: 'planned',
    },
  });

  const editForm = useForm<EditProductionRunFormData>({
    resolver: zodResolver(editProductionRunSchema),
  });

  const createProductionRunMutation = useSecureMutation(
    async (data: ProductionRunFormData) => {
      // Check if recipe exists for the finished product
      const { data: recipe, error: recipeError } = await supabase
        .from('product_recipes')
        .select(`
          quantity_required,
          raw_material:raw_material_id(id, name, current_stock, committed_stock)
        `)
        .eq('finished_product_id', data.finished_product_id);

      if (recipeError) throw recipeError;

      if (!recipe || recipe.length === 0) {
        throw new Error('No recipe found for this finished product');
      }

      // Check if there's enough available stock (total - committed) for all raw materials
      const stockChecks = recipe.map(item => {
        const requiredQuantity = item.quantity_required * data.quantity_to_produce;
        const rawMaterial = item.raw_material as any;
        const availableStock = rawMaterial.current_stock - (rawMaterial.committed_stock || 0);
        
        if (availableStock < requiredQuantity) {
          throw new Error(
            `Insufficient available stock for ${rawMaterial.name}. Required: ${requiredQuantity}, Available: ${availableStock} (Total: ${rawMaterial.current_stock}, Committed: ${rawMaterial.committed_stock || 0})`
          );
        }
        
        return {
          raw_material_id: rawMaterial.id,
          raw_material_name: rawMaterial.name,
          quantity_required: requiredQuantity,
          current_stock: rawMaterial.current_stock,
          committed_stock: rawMaterial.committed_stock || 0
        };
      });

      // Create the production run - backend triggers will handle inventory management
      const { data: productionRun, error: productionError } = await supabase
        .from('production_runs')
        .insert([{
          finished_product_id: data.finished_product_id,
          quantity_to_produce: data.quantity_to_produce,
          status: data.status,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        }])
        .select()
        .single();

      if (productionError) throw productionError;

      return productionRun;
    },
    {
      onSuccess: async () => {
        // Invalidate and refetch all related queries
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['production-runs'] }),
          queryClient.invalidateQueries({ queryKey: ['production-dashboard'] }),
          queryClient.invalidateQueries({ queryKey: ['inventory-items'] }),
          queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] }),
          refetchProductionRuns(), // Force immediate refetch
        ]);
        toast.success('Production run created successfully');
        setIsDialogOpen(false);
        form.reset();
      },
      onError: (error: any) => {
        toast.error(`Failed to create production run: ${error.message || 'Unknown error'}`);
        console.error('Error creating production run:', error);
      },
    }
  );

  const updateStatusMutation = useSecureMutation(
    async ({ runId, newStatus }: { runId: string; newStatus: string }) => {
      // Check if this run is already being processed
      if (processingRunIds.has(runId)) {
        throw new Error('This production run is already being updated. Please wait for the current operation to complete.');
      }
      
      // Add to processing set
      setProcessingRunIds(prev => new Set(prev).add(runId));
      
      try {
        const updateData: any = { status: newStatus };
        
        if (newStatus === 'in_progress') {
          updateData.started_at = new Date().toISOString();
        } else if (newStatus === 'completed') {
          // Only set completion time - let the trigger handle inventory and quantity_produced
          updateData.completed_at = new Date().toISOString();
          // Remove quantity_produced update to prevent trigger conflicts
        }
        
        const { data, error } = await supabase
          .from('production_runs')
          .update(updateData)
          .eq('id', runId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } finally {
        // Always remove from processing set
        setProcessingRunIds(prev => {
          const next = new Set(prev);
          next.delete(runId);
          return next;
        });
      }
    },
    {
      onSuccess: async () => {
        // Invalidate and refetch all related queries
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['production-runs'] }),
          queryClient.invalidateQueries({ queryKey: ['production-dashboard'] }),
          queryClient.invalidateQueries({ queryKey: ['inventory-items'] }),
          queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] }),
          refetchProductionRuns(),
        ]);
        toast.success('Production run status updated successfully');
        setIsEditDialogOpen(false);
      },
      onError: (error: any) => {
        toast.error(`Failed to update status: ${error.message || 'Unknown error'}`);
      },
    }
  );

  const editProductionRunMutation = useSecureMutation(
    async (data: EditProductionRunFormData & { runId: string }) => {
      // Check if this run is already being processed
      if (processingRunIds.has(data.runId)) {
        throw new Error('This production run is already being updated. Please wait for the current operation to complete.');
      }
      
      // Add to processing set
      setProcessingRunIds(prev => new Set(prev).add(data.runId));
      
      try {
        const updateData: any = { 
          status: data.status,
        };
        
        // Only allow quantity changes if status is 'planned'
        if (data.quantity_to_produce && editingRun?.status === 'planned') {
          updateData.quantity_to_produce = data.quantity_to_produce;
        }
        
        // Add notes if provided (for cancellation reasons, etc.)
        if (data.notes && data.notes.trim()) {
          updateData.notes = data.notes.trim();
        }
        
        // Set timestamps based on status
        if (data.status === 'in_progress' && editingRun?.status === 'planned') {
          updateData.started_at = new Date().toISOString();
        } else if (data.status === 'completed' && editingRun?.status !== 'completed') {
          updateData.completed_at = new Date().toISOString();
        } else if (data.status === 'cancelled') {
          updateData.completed_at = new Date().toISOString();
        }
        
        const { data: result, error } = await supabase
          .from('production_runs')
          .update(updateData)
          .eq('id', data.runId)
          .select()
          .single();

        if (error) throw error;
        return result;
      } finally {
        // Always remove from processing set
        setProcessingRunIds(prev => {
          const next = new Set(prev);
          next.delete(data.runId);
          return next;
        });
      }
    },
    {
      onSuccess: async () => {
        // Invalidate and refetch all related queries
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['production-runs'] }),
          queryClient.invalidateQueries({ queryKey: ['production-dashboard'] }),
          queryClient.invalidateQueries({ queryKey: ['inventory-items'] }),
          queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] }),
          refetchProductionRuns(),
        ]);
        toast.success('Production run updated successfully');
        setIsEditDialogOpen(false);
        editForm.reset();
      },
      onError: (error: any) => {
        toast.error(`Failed to update production run: ${error.message || 'Unknown error'}`);
      },
    }
  );

  const onSubmit = (data: ProductionRunFormData) => {
    createProductionRunMutation.mutate(data);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      planned: {
        variant: 'secondary' as const,
        icon: Clock,
        className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300',
      },
      in_progress: {
        variant: 'default' as const,
        icon: Play,
        className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300',
      },
      completed: {
        variant: 'default' as const,
        icon: CheckCircle,
        className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300',
      },
      cancelled: {
        variant: 'destructive' as const,
        icon: XCircle,
        className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300',
      },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || {
      variant: 'outline' as const,
      icon: AlertCircle,
      className: '',
    };
    
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={`gap-1 ${config.className}`}>
        <Icon className="h-3 w-3" />
        {status?.toUpperCase() || 'UNKNOWN'}
      </Badge>
    );
  };

  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case 'planned':
        return 'in_progress';
      case 'in_progress':
        return 'completed';
      default:
        return null;
    }
  };

  const getStatusButtonText = (currentStatus: string) => {
    switch (currentStatus) {
      case 'planned':
        return 'Start Production';
      case 'in_progress':
        return 'Mark Complete';
      default:
        return null;
    }
  };

  const getStatusButtonIcon = (currentStatus: string) => {
    switch (currentStatus) {
      case 'planned':
        return Play;
      case 'in_progress':
        return CheckCircle;
      default:
        return null;
    }
  };

  const getProgressPercentage = (status: string) => {
    switch (status) {
      case 'planned':
        return 0;
      case 'in_progress':
        return 50;
      case 'completed':
        return 100;
      case 'cancelled':
        return 0;
      default:
        return 0;
    }
  };

  const handleEditRun = (run: any) => {
    setEditingRun(run);
    // Pre-populate the edit form with current values
    editForm.reset({
      status: run.status,
      quantity_to_produce: run.quantity_to_produce,
      notes: run.notes || '',
    });
    setIsEditDialogOpen(true);
  };

  const onEditSubmit = (data: EditProductionRunFormData) => {
    if (!editingRun) return;
    editProductionRunMutation.mutate({
      ...data,
      runId: editingRun.id,
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Production Runs
            </CardTitle>
            <CardDescription>
              Manufacturing and assembly operations
            </CardDescription>
          </div>
          {canModify && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Production Run
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Create Production Run</DialogTitle>
                  <DialogDescription>
                    Schedule a new production run for a finished product.
                  </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="finished_product_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Finished Product</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a finished product" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {finishedProducts.map((product) => (
                                <SelectItem key={product.id} value={product.id}>
                                  <div className="flex items-center gap-2">
                                    <ProductThumbnail 
                                      imageUrl={product.image_url} 
                                      name={product.name} 
                                      size="sm"
                                    />
                                    {product.name} ({product.sku})
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="quantity_to_produce"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Quantity to Produce</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="1"
                              step="1"
                              placeholder="Enter quantity"
                              {...field}
                              onChange={(e) => field.onChange(Number(e.target.value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="planned">Planned</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={createProductionRunMutation.isPending}>
                        {createProductionRunMutation.isPending ? 'Creating...' : 'Create Production Run'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          )}
          
          {canModify && (
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Edit Production Run</DialogTitle>
                  <DialogDescription>
                    Update the production run details, status, or add cancellation notes.
                  </DialogDescription>
                </DialogHeader>
                {editingRun && (
                  <div className="space-y-6">
                    {/* Product Info */}
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <ProductThumbnail 
                        imageUrl={(editingRun.finished_product as any)?.image_url} 
                        name={(editingRun.finished_product as any)?.name || 'Unknown Product'} 
                        size="sm"
                      />
                      <div>
                        <p className="font-medium">{(editingRun.finished_product as any)?.name}</p>
                        <p className="text-sm text-muted-foreground">
                          SKU: {(editingRun.finished_product as any)?.sku}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Created: {format(new Date(editingRun.created_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    </div>

                    {/* Edit Form */}
                    <Form {...editForm}>
                      <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
                        {/* Status Selection */}
                        <FormField
                          control={editForm.control}
                          name="status"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Status</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select status" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="planned">Planned</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Quantity (only editable if status is planned) */}
                        <FormField
                          control={editForm.control}
                          name="quantity_to_produce"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Quantity to Produce</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min="1"
                                  step="1"
                                  placeholder="Enter quantity"
                                  disabled={editingRun.status !== 'planned'}
                                  {...field}
                                  onChange={(e) => field.onChange(Number(e.target.value))}
                                />
                              </FormControl>
                              {editingRun.status !== 'planned' && (
                                <p className="text-xs text-muted-foreground">
                                  Quantity can only be modified when status is "Planned"
                                </p>
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Notes/Reason */}
                        <FormField
                          control={editForm.control}
                          name="notes"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Notes / Reason</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Add notes or reason for changes (e.g., cancellation reason)..."
                                  className="min-h-[80px]"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => setIsEditDialogOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button 
                            type="submit" 
                            disabled={editProductionRunMutation.isPending}
                          >
                            {editProductionRunMutation.isPending ? 'Updating...' : 'Update Production Run'}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Planned Qty</TableHead>
              <TableHead>Produced Qty</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Completed</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center">No production runs found</TableCell>
              </TableRow>
            ) : (
              runs.map((run) => (
                <TableRow key={run.id} className="hover:bg-muted/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <ProductThumbnail 
                        imageUrl={(run.finished_product as any)?.image_url} 
                        name={(run.finished_product as any)?.name || 'Unknown Product'} 
                        size="sm"
                      />
                      <div>
                        <span className="font-medium">
                          {(run.finished_product as any)?.name || 'Unknown Product'}
                        </span>
                        <p className="text-sm text-muted-foreground">
                          {(run.finished_product as any)?.sku}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{run.quantity_to_produce}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{run.quantity_produced}</span>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {run.quantity_produced}/{run.quantity_to_produce}
                        </span>
                        <span className="text-muted-foreground">
                          {getProgressPercentage(run.status)}%
                        </span>
                      </div>
                      <Progress 
                        value={getProgressPercentage(run.status)} 
                        className="h-2"
                      />
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(run.status)}</TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {format(new Date(run.created_at), 'MMM dd, yyyy')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {run.started_at ? format(new Date(run.started_at), 'MMM dd, yyyy') : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {run.completed_at ? format(new Date(run.completed_at), 'MMM dd, yyyy') : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {canModify ? (
                      <div className="flex items-center justify-center gap-2">
                        {getNextStatus(run.status) && (() => {
                          const ButtonIcon = getStatusButtonIcon(run.status);
                          const isProcessing = processingRunIds.has(run.id) || updateStatusMutation.isPending;
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateStatusMutation.mutate({ 
                                runId: run.id, 
                                newStatus: getNextStatus(run.status)! 
                              })}
                              disabled={isProcessing}
                              className="gap-1"
                            >
                              {ButtonIcon && <ButtonIcon className="h-3 w-3" />}
                              {isProcessing ? 'Processing...' : getStatusButtonText(run.status)}
                            </Button>
                          );
                        })()}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditRun(run)}
                          disabled={processingRunIds.has(run.id) || updateStatusMutation.isPending || editProductionRunMutation.isPending}
                          className="h-8 w-8 p-0"
                          title="Edit production run"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <span className="text-xs text-muted-foreground">View Only</span>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};